import { useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { Message } from '../lib/api'
import { Button } from './ui/button'
import { cn } from '../lib/utils'

/**
 * VoiceRecorderButton turns the composer into a voice note recorder.
 *
 * MediaRecorder, not a third-party library: every target browser (and
 * Playwright's Chromium, via --use-fake-device-for-media-stream) implements
 * it natively, and a voice note is exactly the "record a clip, get a Blob"
 * shape it was built for — nothing here needs streaming chunks or a custom
 * codec.
 */
export function VoiceRecorderButton({
  conversationId,
  disabled,
  onSent,
}: {
  conversationId: string
  disabled: boolean
  onSent: (result: { message: Message; runs: { agent_message: Message; run_id: string }[] }) => void
}) {
  const [recording, setRecording] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const startedAtRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)

  async function start() {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      // No explicit mimeType: letting the browser pick its own default codec
      // is what makes this work across Chromium, Firefox and Safari without
      // a feature-detection matrix — the server reads Content-Type off the
      // upload rather than assuming webm.
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.start()
      recorderRef.current = recorder
      startedAtRef.current = Date.now()
      setRecording(true)
    } catch {
      setError('Microphone access was denied or is unavailable.')
    }
  }

  function stop() {
    const recorder = recorderRef.current
    if (!recorder) return

    // MediaRecorder.stop() flushes the final chunk asynchronously via
    // ondataavailable/onstop — the Blob is only complete once onstop fires,
    // not the instant stop() returns.
    recorder.onstop = async () => {
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      setRecording(false)

      const durationMs = Date.now() - startedAtRef.current
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
      if (blob.size === 0) return

      setUploading(true)
      try {
        const result = await api.uploadVoice(conversationId, blob, durationMs)
        onSent(result)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not send the voice note.')
      } finally {
        setUploading(false)
      }
    }
    recorder.stop()
    recorderRef.current = null
  }

  return (
    <div className="flex items-center gap-2">
      {error && <p className="max-w-40 text-xs text-destructive">{error}</p>}
      <Button
        type="button"
        variant={recording ? 'default' : 'outline'}
        className={cn(recording && 'bg-destructive text-white hover:bg-destructive/90')}
        disabled={disabled || uploading}
        onClick={recording ? stop : start}
        aria-label={recording ? 'Stop recording' : 'Record a voice note'}
        data-testid={recording ? 'voice-stop' : 'voice-record'}
      >
        {uploading ? '…' : recording ? '⏹' : '🎙'}
      </Button>
    </div>
  )
}
